import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import UPDropdownWithInput from 'components/dropdown-with-input';
import HOUR_OPTIONS from 'modules/contracts/HourDropdownOptions';
import { filterByHour } from 'modules/contracts/helpers';
import { ReactComponent as ClockIcon } from '../../../assets/icons/ClockIcon.svg';
import { ReactComponent as PlannedTimeIcon } from '../../../assets/icons/PlannedTimeIcon.svg';

interface EditableTimeCellProps {
  value: string;
  field: string;
  hoursSource: string;
  hasTimeRegistration: boolean;
  isEmployeeAdjusted: boolean;
  isEditable: boolean;
  onSave: (field: string, value: string) => void;
  icon?: ReactNode;
  hasError?: boolean;
}

const TIME_FORMAT_REGEX = /^\d{2}:\d{2}$/;

const CellWrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'highlighted' && prop !== 'hasError',
})<{ highlighted?: boolean; hasError?: boolean }>(({ highlighted, hasError }) => ({
  width: '100%',
  ...(highlighted && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ff981f !important',
      borderWidth: '2px !important',
    },
  }),
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#EC4A67 !important',
      borderWidth: '2px !important',
    },
  }),
}));

const StyledClockIcon = styled(ClockIcon, {
  shouldForwardProp: prop => prop !== 'highlighted',
})<{ highlighted?: boolean }>(({ highlighted }) => ({
  width: 16,
  height: 16,
  color: highlighted ? '#FF8C00' : '#9EA2A2',
  fill: highlighted ? '#FF8C00' : '#9EA2A2',
}));

const StyledPlannedTimeIcon = styled(PlannedTimeIcon)({
  width: 16,
  height: 16,
  color: '#9EA2A2',
  fill: '#9EA2A2',
});

const EditableTimeCell = ({
  value,
  field,
  hoursSource,
  hasTimeRegistration,
  isEmployeeAdjusted,
  isEditable,
  onSave,
  icon,
  hasError,
}: EditableTimeCellProps): JSX.Element => {
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (newValue: string) => {
      setLocalValue(newValue);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const trimmed = newValue.trim();
        if (trimmed !== value && (trimmed === '' || TIME_FORMAT_REGEX.test(trimmed))) {
          onSave(field, trimmed);
        } else if (trimmed !== '' && !TIME_FORMAT_REGEX.test(trimmed)) {
          setLocalValue(value);
        }
      }, 300);
    },
    [value, field, onSave],
  );

  const isHighlighted =
    isEmployeeAdjusted && hoursSource === 'TimeRegistration' && (field === 'startWork' || field === 'endWork');

  const defaultIcon = hasTimeRegistration ? <StyledClockIcon highlighted={isHighlighted} /> : <StyledPlannedTimeIcon />;

  return (
    <CellWrapper highlighted={isHighlighted} hasError={hasError}>
      <UPDropdownWithInput
        value={localValue}
        handleChange={handleChange}
        items={HOUR_OPTIONS}
        filterOptions={filterByHour}
        label=""
        type={field}
        disabled={!isEditable}
        placeholder=""
        startAdornment={icon ?? defaultIcon}
      />
    </CellWrapper>
  );
};

EditableTimeCell.defaultProps = {
  icon: undefined,
  hasError: false,
};

export default EditableTimeCell;
